// apps/web/src/pages/vendas/importacao/passo-arquivo.tsx — Fase 12, Plano 25 (PV-03, D-30).
// Passo 1 do assistente: cliente, arquivo (.xlsx/.csv, até 10 MB checado no navegador), aba e cabeçalho.
import type { ClienteLookupPv } from '@pieltec/contracts';
import { ClienteCombobox } from '../../../components/vendas/cliente-combobox';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Skeleton } from '../../../components/ui/skeleton';
import { CampoSelect } from '../../../components/ui/campo-select';

interface PassoArquivoProps {
  readonly cliente: ClienteLookupPv | null;
  readonly onCliente: (cliente: ClienteLookupPv) => void;
  readonly arquivo: File | null;
  readonly onArquivo: (arquivo: File | null) => void;
  readonly temCabecalho: boolean;
  readonly onTemCabecalho: (valor: boolean) => void;
  /** Abas da planilha já lida; o seletor só aparece com mais de uma. */
  readonly abas: readonly string[];
  readonly aba: string | null;
  readonly onAba: (aba: string) => void;
  readonly erro: string | null;
  readonly detalheErro: string | null;
  readonly ocupado: boolean;
  readonly onContinuar: () => void;
}

export function PassoArquivo(props: PassoArquivoProps) {
  const { cliente, arquivo, abas, aba, erro, detalheErro, ocupado } = props;
  const pronto = cliente !== null && arquivo !== null && !ocupado;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex max-w-md flex-col gap-1">
        <label htmlFor="importacao-cliente" className="text-xs font-semibold uppercase tracking-wider">
          Cliente
        </label>
        <ClienteCombobox id="importacao-cliente" value={cliente} onChange={props.onCliente} disabled={ocupado} />
      </div>

      <div className="flex max-w-md flex-col gap-1">
        <label htmlFor="importacao-arquivo" className="text-xs font-semibold uppercase tracking-wider">
          Arquivo (.xlsx ou .csv)
        </label>
        <input
          id="importacao-arquivo"
          type="file"
          accept=".xlsx,.csv"
          disabled={ocupado}
          aria-describedby="importacao-arquivo-ajuda"
          onChange={(event) => props.onArquivo(event.target.files?.[0] ?? null)}
          className="ui-control rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
        />
        <p id="importacao-arquivo-ajuda" className="text-sm text-muted-foreground">
          Formatos aceitos: .xlsx e .csv, até 10 MB.
        </p>
        {arquivo && <p className="text-sm">{`${arquivo.name} · ${(arquivo.size / 1024).toFixed(0)} KB`}</p>}
      </div>

      {abas.length > 1 && (
        <div className="flex max-w-md flex-col gap-1">
          <label htmlFor="importacao-aba" className="text-xs font-semibold uppercase tracking-wider">
            Aba da planilha
          </label>
          <CampoSelect
            id="importacao-aba"
            value={aba ?? ''}
            disabled={ocupado}
            onValueChange={props.onAba}
            options={abas.map((nome) => ({ value: nome, label: nome }))}
          />
          <p className="text-sm text-muted-foreground">Esta planilha tem várias abas. Escolha a aba com os itens e continue.</p>
        </div>
      )}

      <label htmlFor="importacao-cabecalho" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
        <Checkbox id="importacao-cabecalho" checked={props.temCabecalho} disabled={ocupado} onCheckedChange={(marcada) => props.onTemCabecalho(marcada === true)} />
        A primeira linha contém os cabeçalhos
      </label>

      {erro && (
        <div role="alert" className="flex flex-col gap-1 text-sm text-rose-700">
          <p>{erro}</p>
          {detalheErro && <p className="text-muted-foreground">{detalheErro}</p>}
        </div>
      )}

      {ocupado && (
        <div aria-busy="true" className="flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">Lendo planilha…</p>
          <Skeleton className="h-8 w-full max-w-md" />
          <Skeleton className="h-8 w-full max-w-md" />
        </div>
      )}

      <div>
        <Button type="button" disabled={!pronto} onClick={props.onContinuar}>
          Continuar para o mapeamento
        </Button>
      </div>
    </div>
  );
}
